import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PremiumCard, PremiumPage, PrimaryButton, SectionHeader } from '../components/premium';
import { api } from '../lib/api';
import { normalizeActivityType, WORKOUT_ACTIVITY_TYPES } from '../lib/workoutActivity';
import { formatVolume, workoutSetCount } from '../lib/workoutStats';
import { formatDistanceM, formatIntervalLine, primaryDistanceM } from '../lib/cardioDraft';
import { formatPace, paceSecPerKm } from '../lib/cardioTracking';
import { calorieSourceLabel } from '../lib/cardioCalories';
import { kgToLbs } from '../lib/units';
import { useAuth } from '../auth/AuthContext';
import type { Workout } from '../types';

function formatSetLine(
  s: Workout['exercises'][0]['sets'][0],
  imperial: boolean,
): string {
  const isTime = s.trackMode === 'time' || ((s.durationSeconds ?? 0) > 0 && s.reps <= 0);
  if (isTime) {
    const sec = s.durationSeconds ?? 0;
    return `${sec}s${s.rpe != null ? ` @ RPE ${s.rpe}` : ''}`;
  }
  if (s.isBodyweight) return `${s.reps} reps BW${s.rpe != null ? ` · RPE ${s.rpe}` : ''}`;
  const w = imperial ? Math.round(kgToLbs(s.weightKg) * 10) / 10 : Math.round(s.weightKg * 10) / 10;
  const unit = imperial ? 'lb' : 'kg';
  return `${w} ${unit} × ${s.reps}${s.rpe != null ? ` · RPE ${s.rpe}` : ''}${s.rir != null ? ` · RIR ${s.rir}` : ''}`;
}

export function WorkoutDetailPage() {
  const { workoutId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const imperial = user?.preferredUnitSystem === 'imperial';
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<Workout[]>('/api/workouts')
      .then((list) => setWorkout(list.find((w) => w.id === workoutId) ?? null))
      .finally(() => setLoading(false));
  }, [workoutId]);

  async function remove() {
    if (!workout || !window.confirm(`Delete “${workout.name}”?`)) return;
    await api(`/api/workouts/${workout.id}`, { method: 'DELETE' });
    nav('/workouts/history', { replace: true });
  }

  if (loading) {
    return (
      <PremiumPage title="Workout" onBack={() => nav('/workouts')}>
        <p className="text-sm text-[var(--text-muted)]">Loading…</p>
      </PremiumPage>
    );
  }

  if (!workout) {
    return (
      <PremiumPage title="Workout" onBack={() => nav('/workouts/history')}>
        <p className="text-sm text-[var(--text-muted)]">Workout not found.</p>
        <PrimaryButton className="mt-4" onClick={() => nav('/workouts')}>
          Back
        </PrimaryButton>
      </PremiumPage>
    );
  }

  const actLabel =
    WORKOUT_ACTIVITY_TYPES.find((a) => a.id === normalizeActivityType(workout.activityType))?.label ?? 'Training';
  const dateLabel = new Date(workout.startedAt).toLocaleString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <PremiumPage title="Workout" onBack={() => nav('/workouts/history')} backLabel="Back to history">
      <PremiumCard variant="train" className="!p-5">
        <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">{workout.name}</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">{dateLabel}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-[var(--text-secondary)]">
          <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{actLabel}</span>
          {workout.durationMinutes ? (
            <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{workout.durationMinutes} min</span>
          ) : null}
          {formatDistanceM(primaryDistanceM(workout), imperial) ? (
            <span className="rounded-full bg-white/[0.06] px-2.5 py-1">
              {formatDistanceM(primaryDistanceM(workout), imperial)}
            </span>
          ) : null}
          {workout.estimatedCaloriesBurned ? (
            <span className="rounded-full bg-white/[0.06] px-2.5 py-1">
              ~{workout.estimatedCaloriesBurned} kcal
              {workout.caloriesSource ? ` · ${calorieSourceLabel(workout.caloriesSource)}` : ''}
            </span>
          ) : null}
          {workoutSetCount(workout) > 0 ? (
          <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{workoutSetCount(workout)} sets</span>
          ) : null}
          {formatVolume(workout, imperial) ? (
            <span className="rounded-full bg-white/[0.06] px-2.5 py-1">{formatVolume(workout, imperial)}</span>
          ) : null}
        </div>
        {workout.notes ? (
          <p className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5 text-sm leading-relaxed text-[var(--text-secondary)]">
            {workout.notes}
          </p>
        ) : null}
        <div className="mt-4 flex gap-2">
          <PrimaryButton className="flex-1" onClick={() => nav(`/workouts/edit/${workout.id}`)}>
            Edit
          </PrimaryButton>
          <PrimaryButton variant="danger" onClick={() => void remove()}>
            Delete
          </PrimaryButton>
        </div>
      </PremiumCard>

      {(workout.cardioSegments?.length ?? 0) > 0 ? (
        <>
          <SectionHeader title={workout.sessionKind === 'multisport' ? 'Segments' : 'Cardio'} />
          <ul className="space-y-3">
            {workout.cardioSegments!.map((seg) => {
              const pace = formatPace(paceSecPerKm(seg.distanceMeters, seg.durationSeconds), imperial);
              const zm = seg.zoneMinutes;
              const rx = formatIntervalLine(seg.intervalPrescription, imperial);
              return (
                <PremiumCard key={seg.id} className="!p-4">
                  <h3 className="font-display text-base font-bold text-[var(--text-primary)]">
                    {seg.isTransition
                      ? 'Transition'
                      : WORKOUT_ACTIVITY_TYPES.find((a) => a.id === seg.activityType)?.label ?? seg.activityType}
                  </h3>
                  <ul className="mt-2 space-y-1 text-sm text-[var(--text-secondary)]">
                    {seg.durationSeconds ? <li>{Math.round(seg.durationSeconds / 60)} min</li> : null}
                    {formatDistanceM(seg.distanceMeters, imperial) ? (
                      <li>
                        {formatDistanceM(seg.distanceMeters, imperial)}
                        {pace !== '—' ? ` · ${pace}` : ''}
                      </li>
                    ) : null}
                    {seg.avgHr ? <li>Avg HR {seg.avgHr}{seg.maxHr ? ` · max ${seg.maxHr}` : ''}</li> : null}
                    {seg.cadence ? <li>Cadence {seg.cadence}</li> : null}
                    {seg.elevGainM ? <li>+{Math.round(seg.elevGainM)} m</li> : null}
                    {zm && (zm.z1 || zm.z2 || zm.z3 || zm.z4 || zm.z5) ? (
                      <li>
                        Zones {[zm.z1, zm.z2, zm.z3, zm.z4, zm.z5].map((m, i) => (m ? `Z${i + 1} ${m}m` : null)).filter(Boolean).join(' · ')}
                      </li>
                    ) : null}
                    {rx ? <li>{rx}</li> : null}
                    {seg.avgPowerW ? <li>{Math.round(seg.avgPowerW)} W{seg.tss ? ` · TSS ${seg.tss}` : ''}</li> : null}
                    {seg.stroke || seg.swolf || seg.poolLengthM ? (
                      <li>
                        {[seg.poolLengthM ? `${seg.poolLengthM}m pool` : null, seg.stroke, seg.swolf ? `SWOLF ${seg.swolf}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </li>
                    ) : null}
                    {seg.rpe != null ? <li>RPE {seg.rpe}</li> : null}
                  </ul>
                  {seg.laps?.length ? (
                    <ul className="mt-2 space-y-1 border-t border-white/8 pt-2 text-xs text-[var(--text-muted)]">
                      {seg.laps.map((lap, i) => (
                        <li key={lap.id}>
                          {i + 1}. {lap.kind}
                          {lap.durationSeconds ? ` ${lap.durationSeconds}s` : ''}
                          {formatDistanceM(lap.distanceMeters, imperial) ? ` ${formatDistanceM(lap.distanceMeters, imperial)}` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </PremiumCard>
              );
            })}
          </ul>
        </>
      ) : null}

      <SectionHeader title="Exercises" />
          {workout.exercises.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          {(workout.cardioSegments?.length ?? 0) > 0
            ? 'No lifting sets on this session.'
            : workout.activityType && workout.activityType !== 'weightlifting'
              ? 'Logged as time only — no distance or heart rate on this session.'
              : 'No resistance details logged.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {workout.exercises.map((ex) => (
            <PremiumCard key={ex.id} className="!p-4">
              <h3 className="font-display text-base font-bold text-[var(--text-primary)]">{ex.name}</h3>
              <ul className="mt-2 space-y-1.5">
                {ex.sets.map((s, i) => (
                  <li key={s.id} className="flex gap-2 text-sm text-[var(--text-secondary)]">
                    <span className="w-6 shrink-0 font-bold text-[var(--text-muted)]">{i + 1}</span>
                    <span>{formatSetLine(s, imperial)}</span>
                  </li>
                ))}
              </ul>
            </PremiumCard>
          ))}
        </ul>
      )}
    </PremiumPage>
  );
}
